<script lang="ts">
/** One event on the timeline. */
export interface GaiaTimelineItem {
  key: string;
  label: string;
  /** A line under the label — who made it happen, say ("Sent for signing by Doruk Arslan"). */
  detail?: string;
  /** Formatted timestamp under the label. */
  time?: string;
  /**
   * Filled dot = it happened; hollow = still ahead. The view decides, so the
   * timeline never reads a backend status itself.
   */
  done?: boolean;
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaTimelineItem[]>,
    required: true,
  },
});

const ink = gaiaNeutral.ink;
const line = gaiaNeutral.borderDivider;
const surface = gaiaNeutral.surface;
const idle = gaiaNeutral.borderDashed;
</script>

<template>
  <!-- Activity trail on the signature-request detail: a dot per event with a
       connector running to the next one. naive's n-timeline draws its own
       chrome (icons, a heavier rail) and can't be retuned to this hairline, so
       the two-column grid is ours. -->
  <div class="gaia-timeline">
    <div
      v-for="(item, index) in props.items"
      :key="item.key"
      class="event"
      :class="{ last: index === props.items.length - 1 }"
    >
      <n-flex vertical align="center" :size="0">
        <span class="dot" :class="{ done: item.done }" />
        <span v-if="index < props.items.length - 1" class="line" />
      </n-flex>
      <div class="body">
        <!-- The default is label + timestamp; the CM view's prompt history
             slots a whole card in here instead, on the same rail. -->
        <slot name="item" :item="item" :index="index">
          <gaia-text
            size="body"
            :weight="item.done ? 'semibold' : 'regular'"
            :tone="item.done ? 'ink' : 'tertiary'"
          >
            {{ item.label }}
          </gaia-text>
          <gaia-text v-if="item.detail" size="dense" tone="tertiary" :style="{ display: 'block' }">
            {{ item.detail }}
          </gaia-text>
          <gaia-text
            v-if="item.time"
            size="meta"
            tone="faint"
            :style="{ display: 'block', fontVariantNumeric: 'tabular-nums' }"
          >
            {{ item.time }}
          </gaia-text>
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.event {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 12px;
}

.dot {
  width: 10px;
  height: 10px;
  margin-top: 4px;
  flex: none;
  border-radius: 50%;
  border: 2px solid v-bind(idle);
  background: v-bind(surface);
}

.dot.done {
  border-color: v-bind(ink);
  background: v-bind(ink);
}

/* The connector fills whatever height the event's copy takes. */
.line {
  flex: 1;
  width: 1px;
  margin: 3px 0;
  background: v-bind(line);
}

.body {
  padding-bottom: 18px;
}

.event.last .body {
  padding-bottom: 0;
}
</style>
